import React, { useCallback, useEffect, useState } from "react";
import { Col, Row } from "@amzn/stencil-react-components/layout";
import { Button, ButtonSize, ButtonVariant } from "@amzn/stencil-react-components/button";
import { Flyout, FlyoutContent, FlyoutTitleRenderer } from "@amzn/stencil-react-components/flyout";
import { Spinner, SpinnerSize } from "@amzn/stencil-react-components/spinner";
import { MessageBanner, MessageBannerType } from "@amzn/stencil-react-components/message-banner";
import { StartDateCardHeader } from "./start-date-header";
import { FirstDayHeader } from "./ui-redesign/first-day-header";
import { PreHireAppointmentCardHeader } from "./pre-hire-header";
import { SdaDetails } from "../sda/sda-details";
import { PreHireAppointmentDetails } from "./pre-hire-details";
import { PreHireAppointmentDetails as NewPreHireAppointmentDetails } from "./ui-redesign/pre-hire-details";
import { FirstDayDetails } from "./ui-redesign/first-day-details";
import { CSSHeader } from "./css-header";
import { JobDetailsModel, NheDetails, SdaInfo } from "../../utility/application-data";
import useArb from "../../hooks/use-arb";
import { getProcessedJCAddress, isVirtualJob } from "../../utility/jc-response-helper";
import { getRegionFromCookie, getSupportedLanguage } from "../../utility/locale-helper";
import { PreHireEventHeader } from "./ui-redesign/pre-hire-event-header";
import ICalendarLink from "react-icalendar-link";
import { Link } from "@amzn/stencil-react-components/link";
import "moment-timezone";
import preDetailsHeaderImage from "./ui-redesign/pre-details-header.png";
import { GetCalendarEvent } from "../../utility/calendar-event";
import { DisplayNewUIComponent } from "../../utility/new-ui-toggle";
import { Stage } from "../../helpers/get-settings";
import { ScreeningResultsProgressControlled } from "./ui-redesign/screening-result-progress";
import { ContingencyResponse } from "../screening-results/card-uncontrolled";
import { I9Response } from "../i9-task/i9-controlled";
import { ScreeningResultsControlledProperty } from "../screening-results/card-controlled";
import { ShiftNoAvailableHeader } from "./ui-redesign/shift-unavailable";
import { HVH_COLORS } from "../../config/palette";
import { H2 } from "../typography";
import { GoogleMapLinkWithDirectionButton } from "../google-map-link";
import { useParams } from "react-router-dom";
import { isTentativeStartDateByApplicationState, isCountryWithVirtualNHE } from "../../helpers/utils";
import { LAUNCHED_COUNTRY } from "../../config/appConfigConstants";

export enum NHEAppointmentStatusEnum {
  COMPLETED = "COMPLETED",
  RESERVED = "RESERVED",
}
interface AppointmentCardControlledProps {
  sdaDetails?: SdaInfo;
  jcDetails?: JobDetailsModel;
  nheDetails?: NheDetails;
  displayNHE?: boolean;
  scheduleIdFound?: boolean;
  displayScreening?: boolean;
  dtResponse?: ContingencyResponse;
  bgcResponse?: ContingencyResponse;
  airBgcResponse?: ContingencyResponse;
  i9Response?: I9Response;
  stage?: Stage;
  cssURLWithoutScheduleId?: string;
  appointmentSelfServiceURL: string;
  loading?: boolean;
  error?: boolean;
  eligibleForCSS: boolean;
  isSalesforce: boolean;
  eligibleForAppointmentRescheduling: boolean;
  onRetry: () => void;
  onViewNHEDetailsClick: () => void;
  onViewStartDateDetailsClick: () => void;
  onViewListOfDocsClick: () => void;
}

export const titleRenderer: FlyoutTitleRenderer = ({ titleText }) => (
  <H2 id="modal-title" color={"#FFFFFF"}>
    {titleText}
  </H2>
);

export const AppointmentCardControlled = ({
  sdaDetails,
  jcDetails,
  nheDetails,
  displayNHE,
  scheduleIdFound,
  cssURLWithoutScheduleId,
  appointmentSelfServiceURL,
  onRetry,
  loading,
  error,
  displayScreening,
  dtResponse,
  bgcResponse,
  airBgcResponse,
  i9Response,
  stage,
  eligibleForCSS,
  eligibleForAppointmentRescheduling,
  onViewNHEDetailsClick,
  onViewStartDateDetailsClick,
  onViewListOfDocsClick,
  isSalesforce,
}: AppointmentCardControlledProps) => {
  const bundle = useArb("HVHChecklistPantherResource");

  const { applicationId } = useParams<{
    applicationId: string;
  }>();

  const [isTentativeStart, setIsTentativeStart] = useState(false);
  const [isStartDateDetailsViewOpen, setIsStartDateDetailsViewOpen] = useState(false);
  const [isPreHireDetailsViewOpen, setIsPreHireDetailsViewOpen] = useState(false);

  useEffect(() => {
    isTentativeStartDateByApplicationState(applicationId, sdaDetails).then((state) => {
      setIsTentativeStart(state);
    });
  }, [applicationId, sdaDetails]);

  const openStartDateDetailsView = useCallback(() => {
    setIsStartDateDetailsViewOpen(true);
    onViewStartDateDetailsClick();
  }, [isStartDateDetailsViewOpen]);

  const closeStartDateDetailsView = useCallback(() => {
    setIsStartDateDetailsViewOpen(false);
  }, [isStartDateDetailsViewOpen]);

  const openPreHireDetailsView = useCallback(() => {
    setIsPreHireDetailsViewOpen(true);
    onViewNHEDetailsClick();
  }, [isPreHireDetailsViewOpen]);

  const closePreHireDetailsView = useCallback(() => {
    setIsPreHireDetailsViewOpen(false);
  }, [isPreHireDetailsViewOpen]);

  const loadingText = bundle.getMessage("checklist-General-TaskStatus-Loading");

  const appointmentTitle = bundle.formatMessage("Checklist-CardControlled-PreHireTitle");

  const addToCalendar = bundle.formatMessage("Checklist-AppointmentCard-ButtonText3");

  const getDirections = bundle.formatMessage("Checklist-PreHireDetails-GetDirections");
  const googlePlaceURL = "http://maps.google.com/?q=";

  const preHireCalendarEvent = GetCalendarEvent(nheDetails, appointmentTitle, nheDetails?.address);

  if (loading) {
    if (DisplayNewUIComponent) {
      return (
        <Col alignItems="center" padding={"2rem"} backgroundColor={HVH_COLORS.NEUTRAL_0}>
          <Spinner size={SpinnerSize.Small} loadingText={loadingText} showText={true} />
        </Col>
      );
    }
    return (
      <Col alignItems="center" padding={"1rem"}>
        <Spinner size={SpinnerSize.Small} loadingText={loadingText} showText={true} />
      </Col>
    );
  }

  const errorMsg = bundle.getMessage("Checklist-CardControlled-UnableRetrieve");
  const prehireModalTitle = bundle.getMessage("Checklist-CardControlled-PreHireTitle");
  const detailsTitle = bundle.getMessage("Checklist-CardControlled-DetailsTitle");
  const closeButton = bundle.getMessage("Checklist-General-CloseButtonText");
  const startDayOnSiteTitle = bundle.getMessage("Checklist-CardControlled-FirstDayTitle");
  const retry = bundle.getMessage("Checklist-General-RetryButtonText");

  if (!scheduleIdFound) {
    if (eligibleForCSS) {
      if (DisplayNewUIComponent) {
        return <ShiftNoAvailableHeader cssURLWithoutScheduleId={cssURLWithoutScheduleId} />;
      } else {
        return <CSSHeader cssURLWithoutScheduleId={cssURLWithoutScheduleId} />;
      }
    } else if (!isSalesforce) {
      return null;
    }
  }

  if (error) {
    return null; // Remove and customize the error state if needed
    return (
      <Col gridGap="1rem">
        <MessageBanner type={MessageBannerType.Error}>{errorMsg}</MessageBanner>
        <Row>
          <Button size={ButtonSize.Small} onClick={onRetry}>
            {retry}
          </Button>
        </Row>
      </Col>
    );
  }

  if (!isSalesforce && (!sdaDetails || !nheDetails || !jcDetails)) {
    return null;
  } else if (isSalesforce && !jcDetails) {
    return null;
  }

  const startDateTime = jcDetails?.firstDayOnSiteL10N + " " + jcDetails?.startTimeL10N;
  const vh = window.innerHeight + "px";

  const address = jcDetails && getProcessedJCAddress(jcDetails);
  const isVirtual = isVirtualJob(jcDetails);
  const language = !isSalesforce
    ? (getSupportedLanguage(nheDetails?.supportedLanguage, bundle) as string)
    : (jcDetails?.requiredLanguageFromSalesForce as string);
  const startDayCalendarEvent = {
    title: startDayOnSiteTitle,
    startTime: startDateTime,
    location: address,
  };

  const screeningProgressHeaderProps: ScreeningResultsControlledProperty = {
    loading: true,
    error: false,
    bgcResponse: bgcResponse,
    dtResponse: dtResponse,
    i9Response: i9Response,
    airBgcResponse: airBgcResponse,
    stage: stage,
  };

  const addToCalendarKeyDown = (event: any, classname: string) => {
    if (event.key === " " || event.code === "Space") {
      event.preventDefault();
      (document.querySelector(classname) as any).click();
    }
  };

  if (DisplayNewUIComponent) {
    return (
      <>
        {displayNHE && nheDetails && !displayScreening && (
          <PreHireEventHeader
            nheDetails={nheDetails}
            supportedLanguage={language}
            onViewDetailsClick={openPreHireDetailsView}
            eligibleForAppointmentRescheduling={eligibleForAppointmentRescheduling}
            appointmentSelfServiceURL={appointmentSelfServiceURL}
          />
        )}
        {!displayNHE && displayScreening && (
          <>
            <ScreeningResultsProgressControlled property={screeningProgressHeaderProps} jobDetails={jcDetails} />
          </>
        )}
        {!displayNHE && jcDetails && !displayScreening && (
          <FirstDayHeader
            sdaDetails={sdaDetails}
            jobDetails={jcDetails}
            onViewDetailsClick={openStartDateDetailsView}
            isTentativeStart={isTentativeStart}
          />
        )}

        <Flyout isOpen={isStartDateDetailsViewOpen} close={closeStartDateDetailsView} aria-labelledby={"modal-title"}>
          <div style={{ height: vh }}>
            <FlyoutContent
              titleText={detailsTitle}
              renderTitle={titleRenderer}
              buttons={[
                <Row key="details-buttons" width="100%" justifyContent="space-around" alignItems="center">
                  <Row gridGap="1rem">
                    <Button
                      as={"Col"}
                      size={ButtonSize.Small}
                      onKeyDown={(event) => addToCalendarKeyDown(event, ".add-to-calendar-start-day")}
                      tabIndex={-1}
                    >
                      <ICalendarLink
                        key="calendar-button"
                        filename={startDayOnSiteTitle}
                        event={startDayCalendarEvent}
                        className={".add-to-calendar-start-day"}
                      >
                        {addToCalendar}
                      </ICalendarLink>
                    </Button>
                    <GoogleMapLinkWithDirectionButton isVirtual={isVirtual} address={address} />
                  </Row>
                </Row>,
              ]}
              onCloseButtonClick={closeStartDateDetailsView}
            >
              {jcDetails && (
                <FirstDayDetails
                  startDate={jcDetails.firstDayOnSite}
                  address={address}
                  sdaDetails={sdaDetails}
                  isVirtual={isVirtual}
                />
              )}
            </FlyoutContent>
          </div>
        </Flyout>

        <Flyout isOpen={isPreHireDetailsViewOpen} close={closePreHireDetailsView} aria-labelledby={"modal-title"}>
          <FlyoutContent
            titleText={detailsTitle}
            renderTitle={titleRenderer}
            buttons={[
              <Row key="details-buttons" width="100%" justifyContent="space-around" alignItems="center">
                <Row gridGap="1rem">
                  <Button
                    as={"Col"}
                    size={ButtonSize.Small}
                    onKeyDown={(event) => addToCalendarKeyDown(event, ".add-to-calendar-pre-hire-appointment")}
                    tabIndex={-1}
                  >
                    <ICalendarLink
                      key="calendar-button"
                      filename={appointmentTitle}
                      event={preHireCalendarEvent}
                      className={"add-to-calendar-pre-hire-appointment"}
                    >
                      {addToCalendar}
                    </ICalendarLink>
                  </Button>

                  <Link
                    href={googlePlaceURL + nheDetails?.address}
                    target={"_blank"}
                    tabIndex={-1}
                    aria-hidden={true}
                    className={"get-directions"}
                  />
                  {!isCountryWithVirtualNHE(getRegionFromCookie() as LAUNCHED_COUNTRY) && (
                    <Button
                      variant={ButtonVariant.Primary}
                      size={ButtonSize.Small}
                      onClick={() => (document.querySelector(".get-directions") as any).click()}
                    >
                      {getDirections}
                    </Button>
                  )}
                </Row>
              </Row>,
            ]}
            onCloseButtonClick={closePreHireDetailsView}
          >
            <img src={preDetailsHeaderImage} width="100%" alt="" />

            {nheDetails && (
              <NewPreHireAppointmentDetails
                nheDetails={nheDetails}
                eligibleForAppointmentRescheduling={eligibleForAppointmentRescheduling}
                appointmentSelfServiceURL={appointmentSelfServiceURL}
              />
            )}
          </FlyoutContent>
        </Flyout>
      </>
    );
  } else {
    return (
      <>
        {displayNHE && nheDetails && (
          <PreHireAppointmentCardHeader
            nheDate={nheDetails.nheDate}
            nheTimeRange={nheDetails.nheTimeRange}
            onViewDetailsClick={openPreHireDetailsView}
            onViewListOfDocsClick={onViewListOfDocsClick}
            appointmentSelfServiceURL={appointmentSelfServiceURL}
            eligibleForAppointmentRescheduling={eligibleForAppointmentRescheduling}
            supportedLanguage={language}
          />
        )}

        {!displayNHE && sdaDetails && (
          <StartDateCardHeader
            startDate={sdaDetails.startDate}
            startDateStatus={sdaDetails.startDateStatus}
            startTime={sdaDetails.startTime}
            address={address}
            onViewDetailsClick={openStartDateDetailsView}
          />
        )}
        <Flyout isOpen={isStartDateDetailsViewOpen} close={closeStartDateDetailsView}>
          <div style={{ height: vh }}>
            <FlyoutContent
              titleText={startDayOnSiteTitle}
              buttons={[
                <Button key="close-button" variant={ButtonVariant.Primary} onClick={closeStartDateDetailsView}>
                  {closeButton}
                </Button>,
              ]}
              onCloseButtonClick={closeStartDateDetailsView}
            >
              {sdaDetails && address && (
                <SdaDetails startDateTime={startDateTime} address={address} publicText={sdaDetails.publicText} />
              )}
            </FlyoutContent>
          </div>
        </Flyout>
        <Flyout isOpen={isPreHireDetailsViewOpen} close={closePreHireDetailsView}>
          <div style={{ height: vh }}>
            <FlyoutContent
              titleText={prehireModalTitle}
              buttons={[
                <Button key="close-button" variant={ButtonVariant.Primary} onClick={closePreHireDetailsView}>
                  {closeButton}
                </Button>,
              ]}
              onCloseButtonClick={closePreHireDetailsView}
            >
              {nheDetails && (
                <PreHireAppointmentDetails
                  nheDate={nheDetails.nheDate}
                  nheTimeRange={nheDetails.nheTimeRange}
                  address={nheDetails.address}
                  supportedLanguage={language}
                />
              )}
            </FlyoutContent>
          </div>
        </Flyout>
      </>
    );
  }
};
